import React from "react";
import { Link } from "react-router-dom";
import { Cookie, Settings2 } from "lucide-react";
import { usePageTitle } from "../hooks/usePageTitle";

const Section = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="text-xl sm:text-2xl font-bold text-[#0B2B62]">{title}</h2>
    <div className="mt-3 space-y-3 text-slate-700 leading-relaxed text-[15px]">{children}</div>
  </section>
);

const Table = ({ rows }) => (
  <div className="overflow-x-auto mt-3">
    <table className="min-w-full text-sm border border-slate-200 rounded-lg">
      <thead className="bg-slate-50">
        <tr>
          <th className="px-3 py-2 text-left font-semibold text-slate-700">Categoria</th>
          <th className="px-3 py-2 text-left font-semibold text-slate-700">Finalidade</th>
          <th className="px-3 py-2 text-left font-semibold text-slate-700">Base legal</th>
          <th className="px-3 py-2 text-left font-semibold text-slate-700">Duração</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-slate-200">
            <td className="px-3 py-2 font-semibold text-slate-800">{r[0]}</td>
            <td className="px-3 py-2 text-slate-700">{r[1]}</td>
            <td className="px-3 py-2 text-slate-700">{r[2]}</td>
            <td className="px-3 py-2 text-slate-700">{r[3]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function PoliticaCookies() {
  usePageTitle(
    "Política de Cookies",
    "Como a RAF Direct utiliza cookies e tecnologias similares em conformidade com a LGPD e o Google Consent Mode v2."
  );
  return (
    <div className="bg-white" data-testid="cookies-policy-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500">
          <Cookie className="h-3.5 w-3.5" /> Documento legal
        </div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-[#0B2B62]">Política de Cookies</h1>
        <p className="mt-3 text-sm text-slate-500">
          Última atualização: 05 de fevereiro de 2026 · LGPD + Google Consent Mode v2
        </p>

        <Section title="1. O que são cookies">
          <p>
            Cookies são pequenos arquivos armazenados no seu navegador quando você acessa um site. Eles
            servem para lembrar preferências, manter sua sessão autenticada, medir audiência e apoiar
            ações de marketing. Este documento descreve como a <strong>RAF Direct</strong> utiliza cookies
            e tecnologias similares (localStorage, sessionStorage, pixels de conversão).
          </p>
        </Section>

        <Section title="2. Categorias que utilizamos">
          <Table
            rows={[
              [
                "Essenciais",
                "Autenticação, segurança da sessão (raf_session), preferências mínimas de exibição.",
                "Legítimo interesse / execução de contrato",
                "Sessão / até 90 dias",
              ],
              [
                "Análise / Estatística",
                "Google Analytics (GA4) — entender de forma agregada páginas visitadas, tempo, dispositivos.",
                "Consentimento",
                "Até 24 meses",
              ],
              [
                "Marketing",
                "Google Ads (AW-18377265401) e Meta Pixel — medir conversões e apoiar campanhas de conscientização.",
                "Consentimento",
                "Até 24 meses",
              ],
            ]}
          />
        </Section>

        <Section title="3. Google Consent Mode v2">
          <p>
            A RAF Direct implementa o <strong>Google Consent Mode v2</strong>. Isso significa que, por
            padrão, todo tratamento de cookies de marketing e analytics inicia em <em>denied</em>. As tags
            do Google Ads e Analytics só passam a coletar dados de identificação depois que você aceita as
            categorias correspondentes no banner de consentimento — em conformidade com a LGPD.
          </p>
        </Section>

        <Section title="4. Como gerenciar seu consentimento">
          <p>
            Você pode ajustar sua escolha a qualquer momento clicando em
            <button
              type="button"
              onClick={() => window.rafConsent && window.rafConsent.open && window.rafConsent.open()}
              className="mx-1 inline-flex items-center gap-1 text-[#0B2B62] underline"
              data-testid="cookies-open-prefs"
            >
              <Settings2 className="h-3.5 w-3.5" /> Preferências de cookies
            </button>
            (também disponível no rodapé de todas as páginas). Sua escolha fica registrada localmente no
            seu navegador e é aplicada automaticamente nas próximas visitas.
          </p>
          <p>
            Você também pode desativar cookies diretamente pelas configurações do seu navegador (Chrome,
            Firefox, Safari, Edge). Note que a recusa de cookies essenciais pode comprometer o
            funcionamento de áreas autenticadas (painel do consultor, família, clínica, profissional).
          </p>
        </Section>

        <Section title="5. Cookies de terceiros">
          <p>
            Ferramentas de terceiros com quem trabalhamos podem depositar cookies próprios, sob suas
            políticas específicas:
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><a href="https://policies.google.com/technologies/cookies" target="_blank" rel="noreferrer" className="text-[#0B2B62] underline">Google (Analytics, Ads, reCAPTCHA)</a></li>
            <li><a href="https://www.facebook.com/policies/cookies/" target="_blank" rel="noreferrer" className="text-[#0B2B62] underline">Meta / Facebook Pixel</a></li>
            <li><a href="https://stripe.com/cookies-policy/legal" target="_blank" rel="noreferrer" className="text-[#0B2B62] underline">Stripe (checkout de pagamentos)</a></li>
          </ul>
        </Section>

        <Section title="6. Legislação aplicável">
          <p>
            Esta política observa a <strong>LGPD (Lei 13.709/2018)</strong>, o
            <strong> Marco Civil da Internet</strong> (Lei 12.965/2014) e as orientações da
            <strong> Autoridade Nacional de Proteção de Dados (ANPD)</strong> sobre cookies e tecnologias
            similares (Guia Orientativo, 2024).
          </p>
        </Section>

        <Section title="7. Contato">
          <p>
            Dúvidas? Escreva para o Encarregado (DPO) em
            {" "}<a href="mailto:contato@rafdirect.com.br" className="text-[#0B2B62] underline">contato@rafdirect.com.br</a>
            {" "}ou consulte a <Link to="/politica-de-privacidade" className="text-[#0B2B62] underline">Política de Privacidade</Link>.
          </p>
        </Section>
      </div>
    </div>
  );
}
